import { supabase } from './supabase';
import { getStartOfDayUTC, getEndOfDayUTC } from './dateUtils';

export interface PipelineStage {
  id: string;
  brand_id: string;
  name: string;
  order_index: number;
  color: string;
  is_active: boolean;
  is_closed_won: boolean;
  is_closed_lost: boolean;
  created_at: string;
  updated_at: string;
}

export interface Opportunity {
  id: string;
  brand_id: string;
  estimate_id: string | null;
  user_id: string | null;
  pipeline_stage_id: string;
  client_name: string;
  company_name: string | null;
  email: string;
  phone: string | null;
  value: number;
  probability: number;
  expected_close_date: string | null;
  source: string;
  notes: string | null;
  custom_fields: any;
  proposal_link: string | null;
  last_contact_date: string | null;
  won_date: string | null;
  lost_date: string | null;
  lost_reason: string | null;
  priority_score: number;
  priority_level: 'hot' | 'warm' | 'cold' | 'inactive';
  engagement_score: number;
  recency_score: number;
  value_score: number;
  days_in_stage: number;
  last_activity_date: string | null;
  activity_count: number;
  stage_changed_at: string;
  created_at: string;
  updated_at: string;
  pipeline_stage?: PipelineStage;
  estimate?: any;
  brand?: {
    id: string;
    name: string;
    logo_url: string | null;
  };
  assigned_user?: {
    id: string;
    email: string;
    full_name: string;
  };
}

export interface OpportunityActivity {
  id: string;
  opportunity_id: string;
  user_id: string | null;
  activity_type: string;
  subject: string | null;
  description: string;
  metadata: any;
  created_at: string;
}

export interface CreateOpportunityInput {
  brand_id: string;
  estimate_id?: string;
  user_id?: string;
  pipeline_stage_id: string;
  client_name: string;
  company_name?: string;
  email: string;
  phone?: string;
  value: number;
  probability?: number;
  expected_close_date?: string;
  source?: string;
  notes?: string;
  proposal_link?: string;
}

export interface UpdateOpportunityInput {
  client_name?: string;
  company_name?: string;
  email?: string;
  phone?: string;
  value?: number;
  probability?: number;
  expected_close_date?: string;
  source?: string;
  notes?: string;
  pipeline_stage_id?: string;
  won_date?: string;
  lost_date?: string;
  lost_reason?: string;
  last_contact_date?: string;
}

export async function getPipelineStages(brandId: string): Promise<PipelineStage[]> {
  try {
    const { data, error } = await supabase
      .from('pipeline_stages')
      .select('*')
      .eq('brand_id', brandId)
      .eq('is_active', true)
      .order('order_index');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching pipeline stages:', error);
    return [];
  }
}

export async function getBrandUsers(brandId: string): Promise<Array<{ id: string; email: string; full_name: string; role: string }>> {
  try {
    const { data: userBrands, error: userBrandsError } = await supabase
      .from('user_brands')
      .select('user_id')
      .eq('brand_id', brandId);

    if (userBrandsError) throw userBrandsError;

    if (!userBrands || userBrands.length === 0) {
      return [];
    }

    const userIds = userBrands.map(ub => ub.user_id);

    const { data, error } = await supabase
      .from('users')
      .select('id, email, full_name, role')
      .in('id', userIds);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching brand users:', error);
    return [];
  }
}

export async function getOpportunities(
  brandId: string,
  startDate?: string,
  endDate?: string,
  filters?: {
    ownedByMe?: boolean;
    userId?: string;
  }
): Promise<Opportunity[]> {
  try {
    let query = supabase
      .from('opportunities')
      .select(`
        *,
        pipeline_stage:pipeline_stages(*),
        estimate:estimates(id, client_name, totals),
        brand:brands(id, name, logo_url),
        assigned_user:users!opportunities_user_id_fkey(id, email, full_name)
      `)
      .eq('brand_id', brandId);

    if (filters?.ownedByMe) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        query = query.eq('user_id', user.id);
      }
    } else if (filters?.userId) {
      query = query.eq('user_id', filters.userId);
    }

    if (startDate) {
      query = query.gte('created_at', getStartOfDayUTC(startDate));
    }
    if (endDate) {
      query = query.lte('created_at', getEndOfDayUTC(endDate));
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching opportunities:', error);
    return [];
  }
}

export async function getAllOpportunities(startDate?: string, endDate?: string): Promise<Opportunity[]> {
  try {
    let query = supabase
      .from('opportunities')
      .select(`
        *,
        pipeline_stage:pipeline_stages(*),
        estimate:estimates(id, client_name, totals),
        brand:brands(id, name, logo_url)
      `);

    if (startDate) {
      query = query.gte('created_at', getStartOfDayUTC(startDate));
    }
    if (endDate) {
      query = query.lte('created_at', getEndOfDayUTC(endDate));
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching all opportunities:', error);
    return [];
  }
}

export async function getOpportunityById(opportunityId: string): Promise<Opportunity | null> {
  try {
    const { data, error } = await supabase
      .from('opportunities')
      .select(`
        *,
        pipeline_stage:pipeline_stages(*),
        estimate:estimates(*)
      `)
      .eq('id', opportunityId)
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching opportunity:', error);
    return null;
  }
}

export async function createOpportunity(input: CreateOpportunityInput): Promise<{ success: boolean; opportunity?: Opportunity; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    const opportunityData = {
      ...input,
      user_id: input.user_id || user?.id || null,
      probability: input.probability || 50,
      source: input.source || 'manual',
      last_contact_date: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('opportunities')
      .insert(opportunityData)
      .select(`
        *,
        pipeline_stage:pipeline_stages(*)
      `)
      .maybeSingle();

    if (error) throw error;

    if (data) {
      await logActivity({
        opportunity_id: data.id,
        user_id: user?.id || null,
        activity_type: 'note',
        subject: 'Opportunity Created',
        description: `Opportunity created with value $${input.value}`,
        metadata: { source: input.source || 'manual' },
      });
    }

    return { success: true, opportunity: data };
  } catch (error) {
    console.error('Error creating opportunity:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create opportunity',
    };
  }
}

export async function updateOpportunity(
  opportunityId: string,
  input: UpdateOpportunityInput
): Promise<{ success: boolean; opportunity?: Opportunity; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    // Get existing opportunity data for comparison
    const { data: existingOpp, error: fetchError } = await supabase
      .from('opportunities')
      .select('*, pipeline_stage:pipeline_stages(*)')
      .eq('id', opportunityId)
      .maybeSingle();

    if (fetchError) {
      console.error('Error fetching existing opportunity:', fetchError);
      return { success: false, error: fetchError.message };
    }

    if (!existingOpp) {
      console.error('Opportunity not found:', opportunityId);
      return { success: false, error: 'Opportunity not found' };
    }

    // Perform the update
    const { data, error } = await supabase
      .from('opportunities')
      .update(input)
      .eq('id', opportunityId)
      .select(`
        *,
        pipeline_stage:pipeline_stages(*)
      `)
      .maybeSingle();

    if (error) {
      console.error('Supabase update error:', error);
      return { success: false, error: error.message };
    }

    if (data && existingOpp) {
      if (input.pipeline_stage_id && input.pipeline_stage_id !== existingOpp.pipeline_stage_id) {
        await logActivity({
          opportunity_id: opportunityId,
          user_id: user?.id || null,
          activity_type: 'stage_change',
          subject: 'Stage Changed',
          description: `Stage changed from ${existingOpp.pipeline_stage?.name} to ${data.pipeline_stage?.name}`,
          metadata: {
            old_stage_id: existingOpp.pipeline_stage_id,
            new_stage_id: input.pipeline_stage_id,
          },
        });
      }

      supabase.rpc('calculate_opportunity_priority_score', {
        p_opportunity_id: opportunityId,
      }).then(() => {
        console.log('Priority score recalculated successfully');
      }).catch((scoreError) => {
        console.error('Error recalculating priority score (non-blocking):', scoreError);
      });
    }

    return { success: true, opportunity: data };
  } catch (error) {
    console.error('Error updating opportunity:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update opportunity',
    };
  }
}

export async function deleteOpportunity(opportunityId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('opportunities')
      .delete()
      .eq('id', opportunityId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting opportunity:', error);
    return false;
  }
}

export async function moveOpportunityToStage(
  opportunityId: string,
  stageId: string
): Promise<boolean> {
  const result = await updateOpportunity(opportunityId, {
    pipeline_stage_id: stageId,
    last_contact_date: new Date().toISOString(),
  });
  return result.success;
}

export async function markOpportunityWon(opportunityId: string): Promise<boolean> {
  try {
    const { data: stages } = await supabase
      .from('pipeline_stages')
      .select('id')
      .eq('is_closed_won', true)
      .maybeSingle();

    if (!stages) return false;

    const result = await updateOpportunity(opportunityId, {
      pipeline_stage_id: stages.id,
      won_date: new Date().toISOString(),
      probability: 100,
    });

    if (result.success) {
      const { data: { user } } = await supabase.auth.getUser();
      await logActivity({
        opportunity_id: opportunityId,
        user_id: user?.id || null,
        activity_type: 'status_change',
        subject: 'Opportunity Won',
        description: 'Proposal accepted - opportunity marked as won',
        metadata: {},
      });
    }

    return result.success;
  } catch (error) {
    console.error('Error marking opportunity as won:', error);
    return false;
  }
}

export async function markOpportunityLost(
  opportunityId: string,
  reason?: string
): Promise<boolean> {
  try {
    const { data: stages } = await supabase
      .from('pipeline_stages')
      .select('id')
      .eq('is_closed_lost', true)
      .maybeSingle();

    if (!stages) return false;

    const result = await updateOpportunity(opportunityId, {
      pipeline_stage_id: stages.id,
      lost_date: new Date().toISOString(),
      lost_reason: reason,
      probability: 0,
    });

    if (result.success) {
      const { data: { user } } = await supabase.auth.getUser();
      await logActivity({
        opportunity_id: opportunityId,
        user_id: user?.id || null,
        activity_type: 'status_change',
        subject: 'Opportunity Lost',
        description: reason || 'Proposal rejected - opportunity marked as lost',
        metadata: { reason },
      });
    }

    return result.success;
  } catch (error) {
    console.error('Error marking opportunity as lost:', error);
    return false;
  }
}

export async function getOpportunityActivities(opportunityId: string): Promise<OpportunityActivity[]> {
  try {
    const { data, error } = await supabase
      .from('opportunity_activities')
      .select('*')
      .eq('opportunity_id', opportunityId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching activities:', error);
    return [];
  }
}

export async function logActivity(input: {
  opportunity_id: string;
  user_id: string | null;
  activity_type: string;
  subject: string | null;
  description: string;
  metadata?: any;
}): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('opportunity_activities')
      .insert({
        ...input,
        metadata: input.metadata || {},
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error logging activity:', error);
    return false;
  }
}

export async function getConsolidatedDashboardMetrics(startDate?: string, endDate?: string): Promise<{
  overall: {
    totalOpportunities: number;
    totalValue: number;
    wonCount: number;
    wonValue: number;
    lostCount: number;
    lostValue: number;
    inProgressCount: number;
    inProgressValue: number;
    conversionRate: number;
    avgOpportunityValue: number;
  };
  byBrand: Array<{
    brandId: string;
    brandName: string;
    totalOpportunities: number;
    totalValue: number;
    wonCount: number;
    wonValue: number;
    conversionRate: number;
    inProgressCount: number;
  }>;
}> {
  try {
    const opportunities = await getAllOpportunities(startDate, endDate);

    const wonOpps = opportunities.filter((o) => o.pipeline_stage?.is_closed_won);
    const lostOpps = opportunities.filter((o) => o.pipeline_stage?.is_closed_lost);
    const inProgressOpps = opportunities.filter(
      (o) => !o.pipeline_stage?.is_closed_won && !o.pipeline_stage?.is_closed_lost
    );

    const totalValue = opportunities.reduce((sum, o) => sum + Number(o.value), 0);
    const wonValue = wonOpps.reduce((sum, o) => sum + Number(o.value), 0);
    const lostValue = lostOpps.reduce((sum, o) => sum + Number(o.value), 0);
    const inProgressValue = inProgressOpps.reduce((sum, o) => sum + Number(o.value), 0);

    const conversionRate =
      wonOpps.length + lostOpps.length > 0
        ? (wonOpps.length / (wonOpps.length + lostOpps.length)) * 100
        : 0;

    const brandMap = new Map<string, any>();
    opportunities.forEach((opp) => {
      if (!opp.brand) return;

      if (!brandMap.has(opp.brand_id)) {
        brandMap.set(opp.brand_id, {
          brandId: opp.brand_id,
          brandName: opp.brand.name,
          opportunities: [],
        });
      }
      brandMap.get(opp.brand_id).opportunities.push(opp);
    });

    const byBrand = Array.from(brandMap.values()).map((brand) => {
      const brandOpps = brand.opportunities;
      const brandWonOpps = brandOpps.filter((o: any) => o.pipeline_stage?.is_closed_won);
      const brandLostOpps = brandOpps.filter((o: any) => o.pipeline_stage?.is_closed_lost);
      const brandInProgressOpps = brandOpps.filter(
        (o: any) => !o.pipeline_stage?.is_closed_won && !o.pipeline_stage?.is_closed_lost
      );

      const brandTotalValue = brandOpps.reduce((sum: number, o: any) => sum + Number(o.value), 0);
      const brandWonValue = brandWonOpps.reduce((sum: number, o: any) => sum + Number(o.value), 0);
      const brandConversionRate =
        brandWonOpps.length + brandLostOpps.length > 0
          ? (brandWonOpps.length / (brandWonOpps.length + brandLostOpps.length)) * 100
          : 0;

      return {
        brandId: brand.brandId,
        brandName: brand.brandName,
        totalOpportunities: brandOpps.length,
        totalValue: brandTotalValue,
        wonCount: brandWonOpps.length,
        wonValue: brandWonValue,
        conversionRate: brandConversionRate,
        inProgressCount: brandInProgressOpps.length,
      };
    });

    return {
      overall: {
        totalOpportunities: opportunities.length,
        totalValue,
        wonCount: wonOpps.length,
        wonValue,
        lostCount: lostOpps.length,
        lostValue,
        inProgressCount: inProgressOpps.length,
        inProgressValue,
        conversionRate,
        avgOpportunityValue: opportunities.length > 0 ? totalValue / opportunities.length : 0,
      },
      byBrand,
    };
  } catch (error) {
    console.error('Error calculating consolidated dashboard metrics:', error);
    return {
      overall: {
        totalOpportunities: 0,
        totalValue: 0,
        wonCount: 0,
        wonValue: 0,
        lostCount: 0,
        lostValue: 0,
        inProgressCount: 0,
        inProgressValue: 0,
        conversionRate: 0,
        avgOpportunityValue: 0,
      },
      byBrand: [],
    };
  }
}

export async function getDashboardMetrics(
  brandId: string,
  startDate?: string,
  endDate?: string,
  filters?: { ownedByMe?: boolean; userId?: string }
): Promise<{
  totalOpportunities: number;
  totalValue: number;
  wonCount: number;
  wonValue: number;
  lostCount: number;
  lostValue: number;
  inProgressCount: number;
  inProgressValue: number;
  conversionRate: number;
  avgOpportunityValue: number;
  opportunitiesByStage: Array<{ stage: string; count: number; value: number; color: string }>;
}> {
  try {
    const opportunities = await getOpportunities(brandId, startDate, endDate, filters);
    const stages = await getPipelineStages(brandId);

    const wonOpps = opportunities.filter((o) => o.pipeline_stage?.is_closed_won);
    const lostOpps = opportunities.filter((o) => o.pipeline_stage?.is_closed_lost);
    const inProgressOpps = opportunities.filter(
      (o) => !o.pipeline_stage?.is_closed_won && !o.pipeline_stage?.is_closed_lost
    );

    const totalValue = opportunities.reduce((sum, o) => sum + Number(o.value), 0);
    const wonValue = wonOpps.reduce((sum, o) => sum + Number(o.value), 0);
    const lostValue = lostOpps.reduce((sum, o) => sum + Number(o.value), 0);
    const inProgressValue = inProgressOpps.reduce((sum, o) => sum + Number(o.value), 0);

    const conversionRate =
      wonOpps.length + lostOpps.length > 0
        ? (wonOpps.length / (wonOpps.length + lostOpps.length)) * 100
        : 0;

    const opportunitiesByStage = stages.map((stage) => {
      const stageOpps = opportunities.filter((o) => o.pipeline_stage_id === stage.id);
      return {
        stage: stage.name,
        count: stageOpps.length,
        value: stageOpps.reduce((sum, o) => sum + Number(o.value), 0),
        color: stage.color,
      };
    });

    return {
      totalOpportunities: opportunities.length,
      totalValue,
      wonCount: wonOpps.length,
      wonValue,
      lostCount: lostOpps.length,
      lostValue,
      inProgressCount: inProgressOpps.length,
      inProgressValue,
      conversionRate,
      avgOpportunityValue: opportunities.length > 0 ? totalValue / opportunities.length : 0,
      opportunitiesByStage,
    };
  } catch (error) {
    console.error('Error calculating dashboard metrics:', error);
    return {
      totalOpportunities: 0,
      totalValue: 0,
      wonCount: 0,
      wonValue: 0,
      lostCount: 0,
      lostValue: 0,
      inProgressCount: 0,
      inProgressValue: 0,
      conversionRate: 0,
      avgOpportunityValue: 0,
      opportunitiesByStage: [],
    };
  }
}

export async function createOpportunityFromEstimate(
  estimateId: string,
  brandId: string,
  proposalLink: string,
  email?: string,
  companyName?: string,
  totalValue?: number
): Promise<{ success: boolean; opportunity?: Opportunity; error?: string }> {
  try {
    const { data: estimate, error: estimateError } = await supabase
      .from('estimates')
      .select('*')
      .eq('id', estimateId)
      .maybeSingle();

    if (estimateError || !estimate) {
      throw new Error('Estimate not found');
    }

    const existing = await supabase
      .from('opportunities')
      .select('id')
      .eq('estimate_id', estimateId)
      .maybeSingle();

    if (existing.data) {
      return { success: true, opportunity: existing.data as any };
    }

    const { data: proposalSentStage } = await supabase
      .from('pipeline_stages')
      .select('id')
      .eq('brand_id', brandId)
      .eq('name', 'Proposal Sent')
      .maybeSingle();

    if (!proposalSentStage) {
      throw new Error('Proposal Sent stage not found');
    }

    return await createOpportunity({
      brand_id: brandId,
      estimate_id: estimateId,
      user_id: estimate.user_id,
      pipeline_stage_id: proposalSentStage.id,
      client_name: estimate.client_name,
      company_name: companyName || estimate.company_name || estimate.client_name,
      email: email || estimate.email || 'client@example.com',
      value: totalValue || estimate.totals?.grandTotal || estimate.totals?.total_due || 0,
      probability: 50,
      source: 'website',
      notes: 'Auto-created from shared proposal',
      proposal_link: proposalLink,
    });
  } catch (error) {
    console.error('Error creating opportunity from estimate:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create opportunity',
    };
  }
}
